import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Save, Lock, Building2, ListChecks, Plus, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [newServiceType, setNewServiceType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", {
        company_name: settings.company_name,
        company_phone: settings.company_phone,
        company_email: settings.company_email,
        service_area: settings.service_area,
        default_invoice_notes: settings.default_invoice_notes,
        default_job_duration: Number(settings.default_job_duration) || 60,
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveServiceTypes = async (next) => {
    try {
      const { data } = await api.put("/settings", { service_types: next });
      setSettings(data);
      toast.success("Service types updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addServiceType = () => {
    const v = newServiceType.trim();
    if (!v) return;
    if (settings.service_types.includes(v)) {
      toast.error("Already in the list");
      return;
    }
    saveServiceTypes([...settings.service_types, v]);
    setNewServiceType("");
  };

  const removeServiceType = (s) => {
    saveServiceTypes(settings.service_types.filter((x) => x !== s));
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (pwForm.next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Password updated");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPwSaving(false);
    }
  };

  if (loading || !settings) return <div className="overline">Loading…</div>;

  const isPasswordUser = user?.provider === "password";

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <div className="overline mb-2">Configuration</div>
        <h1 className="font-display text-4xl font-black tracking-tight leading-none">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Business profile */}
        <form onSubmit={saveProfile} className="bm-card p-5 space-y-3 lg:col-span-2" data-testid="business-profile-card">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-[#e56b42]" />
            <h2 className="overline">Business Profile</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Company Name</label>
              <input className="bm-input" value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} data-testid="settings-company-name" />
            </div>
            <div>
              <label className="overline block mb-2">Service Area</label>
              <input className="bm-input" value={settings.service_area || ""} onChange={(e) => setSettings({ ...settings, service_area: e.target.value })} data-testid="settings-service-area" />
            </div>
            <div>
              <label className="overline block mb-2">Phone</label>
              <input className="bm-input" value={settings.company_phone || ""} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} data-testid="settings-phone" />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input className="bm-input" type="email" value={settings.company_email || ""} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} data-testid="settings-email" />
            </div>
            <div>
              <label className="overline block mb-2">Default Job Duration (min)</label>
              <input className="bm-input" type="number" min="15" step="15" value={settings.default_job_duration || 60} onChange={(e) => setSettings({ ...settings, default_job_duration: e.target.value })} data-testid="settings-job-duration" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Default Invoice Notes</label>
            <textarea rows={2} className="bm-input" value={settings.default_invoice_notes || ""} onChange={(e) => setSettings({ ...settings, default_invoice_notes: e.target.value })} data-testid="settings-invoice-notes" />
          </div>
          <button type="submit" disabled={saving} className="bm-btn bm-btn-primary" data-testid="save-profile-button">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Profile"}
          </button>
        </form>

        {/* Service types */}
        <div className="bm-card p-5 space-y-3" data-testid="service-types-card">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-[#e56b42]" />
            <h2 className="overline">Service Types</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.service_types.map((s) => (
              <span key={s} className="bm-badge bm-badge-sent flex items-center gap-1.5 pl-2 pr-1 py-1">
                {s}
                <button onClick={() => removeServiceType(s)} className="text-[#8ba394] hover:text-[#c83e2d]" data-testid={`remove-service-${s}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <input className="bm-input flex-1" placeholder="e.g. Aeration" value={newServiceType} onChange={(e) => setNewServiceType(e.target.value)} data-testid="new-service-type-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addServiceType(); } }}
            />
            <button onClick={addServiceType} className="bm-btn bm-btn-secondary" data-testid="add-service-type-button">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>

        {/* Password */}
        <div className="bm-card p-5" data-testid="password-card">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-[#e56b42]" />
            <h2 className="overline">Change Password</h2>
          </div>
          {!isPasswordUser ? (
            <p className="text-sm text-[#8ba394]">
              You're signed in with Google. Manage your password through your Google account.
            </p>
          ) : (
            <form onSubmit={changePassword} className="space-y-3" data-testid="password-form">
              <div>
                <label className="overline block mb-2">Current Password</label>
                <input className="bm-input" type="password" required value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="password-current" />
              </div>
              <div>
                <label className="overline block mb-2">New Password</label>
                <input className="bm-input" type="password" required minLength={6} value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="password-new" />
              </div>
              <div>
                <label className="overline block mb-2">Confirm New Password</label>
                <input className="bm-input" type="password" required minLength={6} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="password-confirm" />
              </div>
              <button type="submit" disabled={pwSaving} className="bm-btn bm-btn-primary" data-testid="password-submit">
                <Lock className="w-4 h-4" /> {pwSaving ? "Updating…" : "Update Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
